import React, { useLayoutEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";
import {
  autoUpdate,
  flip,
  hide,
  offset,
  size,
  shift,
  useFloating,
  FloatingPortal,
} from "@floating-ui/react";
import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import ExerciseHistoryList from "./ExerciseHistoryList";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";

const ExerciseHistoryFloating = ({
  isVisible,
  exercise,
  client,
  onClose,
  panelWidth = 320,
  anchorEl,
}) => {
  const { t, i18n } = useTranslation();
  const [isAnchored, setIsAnchored] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const { refs, floatingStyles, middlewareData, update } = useFloating({
    open: isVisible,
    placement: "right-start",
    strategy: "fixed",
    middleware: [
      offset({ mainAxis: 8 }),
      flip({
        fallbackPlacements: [
          "right",
          "right-end",
          "left-start",
          "left",
          "left-end",
          "bottom",
          "top",
        ],
        fallbackStrategy: "initialPlacement",
        padding: 16,
      }),
      shift({ padding: 16, mainAxis: false, crossAxis: true }),
      size({
        padding: 16,
        apply({ availableWidth, availableHeight, elements }) {
          const viewportHeight =
            typeof window !== "undefined"
              ? window.innerHeight
              : availableHeight;
          const cappedHeight = Math.max(
            0,
            Math.min(availableHeight, viewportHeight * 0.5)
          );
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.max(0, availableWidth)}px`,
            maxHeight: `${cappedHeight}px`,
          });
        },
      }),
      hide({ strategy: "referenceHidden" }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    if (!isVisible || !anchorEl) {
      setIsAnchored(false);
      return;
    }
    refs.setReference(anchorEl);
    update();
    setIsAnchored(true);
  }, [isVisible, anchorEl, exercise, refs, update]);

  const isReferenceHidden = middlewareData?.hide?.referenceHidden;
  if (!isVisible || !exercise || !anchorEl || !isAnchored) {
    return null;
  }
  const exerciseName =
    exercise?.getName?.(t, i18n.language) ??
    exercise?.name ??
    t("exercise-string");

  return (
    <FloatingPortal>
      <View
        ref={refs.setFloating}
        style={[
          styles.container,
          {
            width: panelWidth,
            maxHeight: "50vh",
            opacity: isReferenceHidden ? 0 : 1,
            pointerEvents: isReferenceHidden ? "none" : "auto",
          },
          floatingStyles,
        ]}
      >
        <View style={styles.header}>
          <View style={styles.headerTextBlock}>
            <Text numberOfLines={1} style={styles.title}>
              {exerciseName}
            </Text>
            <Text style={styles.subtitle}>{t("history-string")}</Text>
          </View>
          <HoverableTouchableOpacity
            onPress={onClose}
            style={styles.closeButton}
            hoverStyle={undefined}
            onMouseEnter={() => setIsCloseHovered(true)}
            onMouseLeave={() => setIsCloseHovered(false)}
          >
            <Feather
              name="x"
              size={18}
              color={isCloseHovered ? Colors.darkgrayColor : Colors.grayColor}
            />
          </HoverableTouchableOpacity>
        </View>
        <ScrollView style={styles.content} showsVerticalScrollIndicator={true}>
          <ExerciseHistoryList client={client} exercise={exercise} />
        </ScrollView>
      </View>
    </FloatingPortal>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "fixed",
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
    borderWidth: 0.2,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    zIndex: 1000,
    maxWidth: "90vw",
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: Colors.borderColor,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  headerTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  subtitle: {
    fontSize: 10,
    fontWeight: "500",
    color: Colors.grayColor,
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  closeButton: {
    padding: 6,
  },
  content: {
    flexShrink: 1,
    minHeight: 0,
    scrollbarWidth: "thin",
    scrollbarColor: `${Colors.messageLightColor} transparent`,
  },
});

export default ExerciseHistoryFloating;
